import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogComponent } from '@theme/dialog';

/**
 * The zero-query panel's section (B2C-321). Reached only through the Section Rendering
 * API; it is the one place that reads customer.orders. See
 * decisions/b2c-321-empty-search-panel.md.
 */
const EMPTY_SECTION_ID = 'predictive-search-empty';
const EMPTY_SECTION_SELECTOR = '.predictive-search-empty-section';

/**
 * The most "Recently viewed" rows the desktop dropdown shows (B2C-321, D9). 2, not the
 * PDP writer's 4: on a laptop about 650px tall, 4 viewed rows push "Your usual" below
 * the dropdown's fold, and 2 keep both rows in view (Matteo, 2026-10-12). The row shows
 * the first this-many AVAILABLE products of the request (backfill: a sold-out view among
 * the newest gives its place to an older one, Matteo, 2026-10-13), and the skeleton
 * reserves at most this many rows. Its Liquid twin is `recently_viewed_max_rows` in
 * snippets/search-panel-skeleton.liquid (the rows rendered to reserve): change both
 * together.
 */
const RECENTLY_VIEWED_MAX_ROWS = 2;

/**
 * How many viewed ids the row's request asks for: the PDP writer's cap (MAX_PRODUCTS in
 * assets/recently-viewed-products.js, not changed here). The extra ids are the backfill
 * for sold-out or deleted views (B2C-321, D9.3).
 */
const RECENTLY_VIEWED_CANDIDATES = 4;

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * The in-flight zero-query panel load started by loadEmptyState(), or null.
   * @type {AbortController | null}
   */
  #emptyStateLoad = null;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    // No empty-state load here, on idle or on dialog open, and no read of the
    // Recently viewed list (B2C-321, D1a, D9). Horizon prefetched the panel from
    // these two places whenever the list was non-empty; that prefetch would read
    // customer.orders on every page load, and it cost requests on every page view
    // for a panel most shoppers never open. The panel, with its "Recently viewed"
    // row, loads on open only (loadEmptyState()) and on a reset (#resetSearch).
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  /**
   * Loads the zero-query panel the first time the panel opens (B2C-321). Called by
   * openSearch() in sections/casinetto-header.liquid, the one definition of "open".
   *
   * Unlike #resetSearch it never writes the input or the reset button, so a shopper who
   * starts typing while it loads is never cleared. It shares the component's abort
   * controller, so a typed search supersedes it, and it drops its response if the field
   * is no longer empty when it lands. It is a no-op while a load is in flight, after one
   * has succeeded, or while the field holds a query. On failure it removes the skeleton
   * (snippets/search-panel-skeleton.liquid) and the next call retries.
   *
   * It renders the section from the store root, as the mobile overlay does, so the
   * render context is the same on every page.
   *
   * On a component with `data-recently-viewed` (the header's), it reads the Recently
   * viewed list once, sizes the skeleton's "Recently viewed" block to it before the
   * first await, so the dropdown's first painted frame is already the right height,
   * and requests the row with the panel through #composeEmptyState (D9.1, D9.2).
   */
  async loadEmptyState() {
    const { predictiveSearchResults, searchInput } = this.refs;
    if (this.#emptyStateLoaded || this.#emptyStateLoad || searchInput.value.trim()) return;

    const recentIds = this.#getRecentlyViewedIds();
    this.#reserveRecentlyViewedRows(Math.min(recentIds.length, RECENTLY_VIEWED_MAX_ROWS));

    const abortController = this.#createAbortController();
    this.#emptyStateLoad = abortController;

    try {
      const url = new URL(window.Shopify?.routes?.root ?? '/', location.origin);
      const section = await this.#composeEmptyState(url, recentIds, abortController.signal);
      if (!section || abortController.signal.aborted || searchInput.value.trim()) return;

      morph(predictiveSearchResults, section);
      this.#emptyStateLoaded = true;
    } catch (error) {
      if (abortController.signal.aborted) return;
      predictiveSearchResults.querySelector('[data-search-panel-skeleton]')?.remove();
      throw error;
    } finally {
      if (this.#emptyStateLoad === abortController) this.#emptyStateLoad = null;
    }
  }

  /**
   * The ids the "Recently viewed" row asks for (B2C-321, D9.1): from the PDP writer's
   * list, newest first, digits only, at most RECENTLY_VIEWED_CANDIDATES. Empty on a
   * component without `data-recently-viewed` (the Cmd+K modal's), and when the list
   * cannot be read (storage blocked, malformed JSON): a bad list never blocks the panel.
   * @returns {string[]} The product ids.
   */
  #getRecentlyViewedIds() {
    if (!this.hasAttribute('data-recently-viewed')) return [];

    /** @type {unknown} */
    let stored;
    try {
      stored = RecentlyViewed.getProducts();
    } catch {
      return [];
    }
    if (!Array.isArray(stored)) return [];

    const ids = stored.map((id) => String(id)).filter((id) => /^\d+$/.test(id));
    return [...new Set(ids)].slice(0, RECENTLY_VIEWED_CANDIDATES);
  }

  /**
   * Sizes the skeleton's hidden "Recently viewed" block (snippets/search-panel-skeleton.liquid,
   * `recent: true`) to the rows the row can show (ids requested, capped at
   * RECENTLY_VIEWED_MAX_ROWS): hidden at 0, else
   * that many rows shown (B2C-321, D9.2). A no-op once the skeleton has been replaced.
   * @param {number} count - The rows to reserve: min(ids requested, RECENTLY_VIEWED_MAX_ROWS).
   */
  #reserveRecentlyViewedRows(count) {
    const block = this.refs.predictiveSearchResults.querySelector('[data-search-panel-skeleton-recent]');
    if (!(block instanceof HTMLElement)) return;

    block.hidden = count === 0;
    block.querySelectorAll('[data-search-panel-skeleton-recent-row]').forEach((row, index) => {
      if (row instanceof HTMLElement) row.hidden = index >= count;
    });
  }

  /**
   * Fetches the markup for the "Recently viewed" row: the predictive-search section
   * rendered on /search for `id:… OR id:…` (Horizon's request; `type` is the /search
   * parameter). That path reads no customer data (B2C-321, D9.1).
   * @param {string[]} ids - The product ids, newest first.
   * @returns {Promise<string | null>} The markup, or null without a section id.
   */
  async #getRecentlyViewedProductsMarkup(ids) {
    if (!this.dataset.sectionId) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('type', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  /**
   * The one compose step behind loadEmptyState() and #resetSearch (B2C-321, D9.1).
   * Requests the zero-query section and, when there are ids, the "Recently viewed" row,
   * together, and waits for both. A failed section request throws, as before; a failed
   * or empty row request only means no row. Works on parsed documents only and never
   * touches the live DOM, the input or the reset button: the caller checks its own
   * signal and morphs the result once.
   * @param {URL} url - The URL to render the zero-query section from.
   * @param {string[]} recentIds - The ids for the row; empty for no row.
   * @param {AbortSignal} signal - The caller's signal.
   * @returns {Promise<Element | null>} The composed section, or null once aborted.
   */
  async #composeEmptyState(url, recentIds, signal) {
    const [panel, row] = await Promise.allSettled([
      sectionRenderer.getSectionHTML(EMPTY_SECTION_ID, false, url),
      recentIds.length > 0 ? this.#getRecentlyViewedProductsMarkup(recentIds) : Promise.resolve(null),
    ]);
    if (signal.aborted) return null;
    if (panel.status === 'rejected') throw panel.reason;

    const section = new DOMParser().parseFromString(panel.value, 'text/html').querySelector(EMPTY_SECTION_SELECTOR);
    if (!section) throw new Error('No empty section markup found');

    if (row.status === 'rejected') {
      console.warn('[Search panel] Recently viewed row failed', row.reason);
    } else if (row.value) {
      this.#addRecentlyViewedRow(section, row.value);
    }

    return section;
  }

  /**
   * Puts the "Recently viewed" row above the source row, in the parsed section (B2C-321,
   * D9.1, D9.3). Only the row response's `.recently-viewed-wrapper` is used: never its
   * `[data-search-results]` (it would show the "View all results" footer on an empty
   * field), its status region or its outer `#predictive-search-results`. A wrapper with
   * no `li` (every product skipped) is dropped, so no heading sits over no rows. A
   * product in both rows stays under "Recently viewed" and leaves the source row, with
   * no backfill; a source row left with no product loses its title row and "View all".
   * @param {Element} section - The parsed zero-query section.
   * @param {string} rowMarkup - The row response.
   */
  #addRecentlyViewedRow(section, rowMarkup) {
    const rowProducts = new DOMParser()
      .parseFromString(rowMarkup, 'text/html')
      .getElementById('predictive-search-products');
    const wrapper = rowProducts?.querySelector('.recently-viewed-wrapper');
    if (!rowProducts || !wrapper) {
      console.warn('[Search panel] No Recently viewed row in the response');
      return;
    }
    if (!wrapper.querySelector('li')) return;

    // Backfill (D9.3): the request asked for up to RECENTLY_VIEWED_CANDIDATES views and
    // the Liquid skipped the unavailable ones, so the first RECENTLY_VIEWED_MAX_ROWS
    // items left are the newest available views. Only those are shown and de-duplicated.
    Array.from(wrapper.querySelectorAll('li')).slice(RECENTLY_VIEWED_MAX_ROWS).forEach((item) => item.remove());

    const sourceProducts = section.querySelector('#predictive-search-products');
    if (!sourceProducts) {
      // No source row (D7's unreachable case): the row's own container goes in whole.
      section.querySelector('[data-search-panel]')?.append(rowProducts);
      return;
    }

    const viewedIds = new Set(
      Array.from(wrapper.querySelectorAll('li [data-product-id]'), (card) => card.getAttribute('data-product-id'))
    );
    for (const item of sourceProducts.querySelectorAll('li')) {
      const productId = item.querySelector('[data-product-id]')?.getAttribute('data-product-id');
      if (productId && viewedIds.has(productId)) item.remove();
    }
    if (!sourceProducts.querySelector('li')) sourceProducts.replaceChildren();

    sourceProducts.prepend(wrapper);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        // Empty field: the zero-query panel is content, not suggestions, so Tab
        // moves through it natively ("View all", each row's link and add control).
        // With a query typed, Horizon's Tab-cycles-the-rows stays (B2C-321, D8.6).
        if (!this.refs.searchInput.value.trim()) return;
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else if (!this.refs.searchInput.value.trim()) {
          // Empty query: stay on the page with the panel open (B2C-321).
          // preventDefault also cancels the form's implicit submission.
          event.preventDefault();
        } else {
          // The strict URL every search path lands on (B2C-349,
          // assets/search-strict-query.js). preventDefault cancels the form's
          // implicit submission, so this is the one navigation.
          event.preventDefault();
          const query = this.refs.searchInput.value;
          const strictSearch = window.CasinettoSearch;
          if (strictSearch) {
            window.location.href = strictSearch.searchUrl(query);
          } else {
            const searchUrl = new URL(Theme.routes.search_url, location.origin);
            searchUrl.searchParams.set('q', query);
            window.location.href = searchUrl.toString();
          }
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      // The exit removes the focused "Clear", which would drop focus to body (B2C-321, D9.4).
      this.#moveFocusPast(recentlyViewedWrapper);
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
        // De-duplication can have emptied the source row (every product was also
        // viewed). Without the row the dropdown would stand empty: rebuild the panel,
        // which now has no Recently viewed list, so the source row comes back whole.
        if (!this.refs.predictiveSearchResults.querySelector('li')) this.#resetSearch();
      });
    }
  }

  /**
   * Moves focus, without scrolling, to the first visible link or button in the results
   * after `element`: the source row's "View all", or its row 1 link. With none, to the
   * results container (tabindex="-1"). Never to the input: the header's focus filter
   * blurs a focus there that follows no pointerdown or Tab (B2C-321, D9.4).
   * @param {HTMLElement} element - The element about to leave.
   */
  #moveFocusPast(element) {
    const { predictiveSearchResults } = this.refs;
    const next = Array.from(predictiveSearchResults.querySelectorAll('a[href], button:not([disabled])')).find(
      (candidate) =>
        candidate instanceof HTMLElement &&
        !element.contains(candidate) &&
        Boolean(element.compareDocumentPosition(candidate) & Node.DOCUMENT_POSITION_FOLLOWING) &&
        candidate.getClientRects().length > 0
    );
    const target = next instanceof HTMLElement ? next : predictiveSearchResults;
    target.focus({ preventScroll: true });
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('options[prefix]', 'last');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    // Clear, Escape and an emptied field rebuild the whole panel through the same
    // compose step as an open, so on the header's dropdown the "Recently viewed" row
    // comes back with it, in one morph: two loads can never stack two rows
    // (B2C-321, D9.1).
    const section = await this.#composeEmptyState(url, this.#getRecentlyViewedIds(), abortController.signal);
    if (!section || abortController.signal.aborted) return;

    morph(predictiveSearchResults, section);
    // A reset is a successful panel load too, so the next open does not fetch again.
    this.#emptyStateLoaded = true;
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
